import React, { useRef, useState } from "react";
import { Dialog, Button, Typography, TextField, DialogActions, DialogTitle, useTheme } from "@mui/material";
import { useSocketContext } from "context/SocketContext";
import { useAuthContext } from "context/GameAuthContext";
import { useIntl } from "react-intl";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { FileSources, SocketEmitters } from "constants/sockets";

export const DocumentModal: React.FC<{
	isOpen: boolean;
	onClose: () => void;
	data?: {
		fileSource: FileSources;
		payload: any;
	};
}> = (props) => {
	const { isOpen, onClose, data } = props;
	const intl = useIntl();
	const [input, setInput] = useState<string>("");
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [locked, setLocked] = useState<boolean>(true);
	const { socket } = useSocketContext();
	const { user } = useAuthContext();
	const theme = useTheme();
	const Icon = locked ? LockIcon : LockOpenIcon;
	async function submit() {
		if (!data) return;

		socket?.emit(SocketEmitters.onUnlockFile, {
			roomId: user?.roomId,
			fileSource: data?.fileSource,
			password: input,
			payload: data?.payload,
		});
	}

	React.useEffect(() => {
		if (!isOpen) {
			setInput("");
		}
	}, [isOpen]);

	const onCloseRef = useRef<() => void>();
	onCloseRef.current = onClose;

	React.useEffect(() => {
		if (!socket) return;

		const onWrongPassword = () => {
			setErrorMessage("Wrong password");
		};
		const onSuccess = () => {
			setLocked(false);
			setErrorMessage("");
			setTimeout(() => onCloseRef.current?.(), 1000);
		};

		socket.on("sendFailedToUpdateFile", onWrongPassword);
		socket.on("successNotificationUpdateFile", onSuccess);
		return () => {
			socket.off("sendFailedToUpdateFile", onWrongPassword);
			socket.off("successNotificationUpdateFile", onSuccess);
		};
	}, [onClose, socket]);

	return (
		<Dialog
			open={isOpen}
			maxWidth="xs"
			fullWidth
			sx={{ padding: "0 10px", ".MuiDialog-paper": { overflowY: "visible" }, minHeight: 200 }}
		>
			<div style={DIALOG_ELEMENT_STYLES.infoIcon(theme)}>
				<Icon
					sx={{
						backgroundColor: locked ? "red" : "#4DAE4F",
						borderRadius: "50px",
						width: 60,
						height: 60,
						padding: "15px",
						color: "white",
					}}
				/>
			</div>
			<DialogTitle sx={{ textAlign: "center", marginTop: "10px" }}>
				<Typography>{intl.formatMessage({ id: "DIALOG_TILE.password.title" })}</Typography>
			</DialogTitle>
			<TextField
				id="field-password"
				onChange={(e) => setInput(e.target.value)}
				label={intl.formatMessage({ id: "DIALOG_TILE.password.content" })}
				sx={{ margin: "0 10px", color: "red" }}
				helperText={errorMessage}
				error={errorMessage ? true : false}
			></TextField>
			<DialogActions sx={DIALOG_ELEMENT_STYLES.dialogActions}>
				<Button
					onClick={() => {
						onClose();
						setErrorMessage("");
					}}
					sx={{ backgroundColor: "#D8D8D8", color: "black", textTransform: "none" }}
				>
					{intl.formatMessage({ id: "GLOBAL.close" })}
				</Button>
				<Button
					id="button-unlock-file"
					onClick={submit}
					sx={DIALOG_ELEMENT_STYLES.button}
					disabled={input ? false : true}
				>
					{intl.formatMessage({ id: "DIALOG_TILE.password.button.unlock" })}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const DIALOG_ELEMENT_STYLES = {
	dialogTitle: {
		textAlign: "center",
		marginTop: 15,
	},
	dialogActions: {
		backgroundColor: "#823BEB99",
		justifyContent: "space-between",
		marginTop: "25px",
	},
	button: {
		textTransform: "none",
		color: "#fff",
		backgroundColor: "#4DAE4F",
		height: 30,
		padding: "15px",
	},
	infoIcon: (theme) => ({
		position: "absolute",
		left: "44.5%",
		top: "-20%",
		width: 60,
		height: 60,
		borderRadius: 50,
		boxShadow: `0 0 2px 4px ${theme.palette.grey[100]}`,
	}),
};
